import { Link, useParams } from 'react-router-dom'
import { useChamado } from '../../../api/queries/chamados'
import { AvisoDeErro, Carregando, EstadoVazio } from '../../../components/Estados'
import { IconeSeta } from '../../../components/Icones'
import { CarimboDeProcedencia } from '../CarimboDeProcedencia'
import { AvisoDeAtualizacaoParada } from './AvisoDeAtualizacaoParada'
import { CabecalhoDoChamado } from './CabecalhoDoChamado'
import { CargasDoChamado } from './CargasDoChamado'
import { PainelDaClassificacao } from './PainelDaClassificacao'
import { PainelDePrazos } from './PainelDePrazos'
import { PainelDoJulgamento } from './PainelDoJulgamento'
import { TEXTOS } from '../../../lib/glossario'
import { enderecoDaLista } from '../../../lib/ultimaBusca'

/**
 * A tela de um chamado — montada para conferir, não para folhear.
 *
 * A ordem dos cartões é a ordem em que as perguntas aparecem: o que é este chamado, quando ele
 * andou e contra qual prazo, como a fonte o classificou, o que a fiscalização julgou — com o apoio
 * da IA dentro do mesmo cartão —, e de onde tudo isso veio. O julgamento não fica atrás de aba
 * nenhuma: aba esconde, e o que se esconde aqui é o julgamento humano que vira glosa.
 *
 * Não há ação nenhuma nesta tela, e isso é o desenho. Chamado não é agregado deste sistema: ele
 * nasce no e-Niterói e entra aqui por uma carga. A única escrita do módulo é a carga da planilha,
 * que fica onde ela pertence — na lista e no fechamento do mês.
 *
 * @returns o detalhe do chamado
 */
export function PaginaDetalheChamado() {
  const { id } = useParams()
  const chamadoId = Number(id)
  const { data: chamado, isPending, isError, error } = useChamado(chamadoId)

  if (!Number.isFinite(chamadoId)) {
    return (
      <EstadoVazio
        titulo={TEXTOS.enderecoInvalido}
        ajuda="Este link não aponta para um chamado. Volte para a lista e abra o registro pelo número do e-Niterói."
        acao={<VoltarParaALista />}
      />
    )
  }
  if (isPending) {
    return <Carregando />
  }
  if (isError) {
    return (
      <div className="space-y-4">
        <AvisoDeErro erro={error} titulo="Não foi possível abrir o chamado" />
        <VoltarParaALista />
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <VoltarParaALista />

      {/* O carimbo acompanha o registro, e não só a lista: quem chega aqui por um link direto —
          que é como um chamado é citado num e-mail — nunca passou pela tela de trás, e a data de
          corte deste registro é a única coisa que impede a leitura de que ele está atualizado. */}
      <CarimboDeProcedencia origens={[chamado.origem]} coletaMaisRecente={chamado.coletadoEm} />
      <AvisoDeAtualizacaoParada chamado={chamado} />

      <CabecalhoDoChamado chamado={chamado} />
      <PainelDePrazos chamado={chamado} />
      <PainelDaClassificacao chamado={chamado} />
      {/* Um cartão só: a leitura da IA, as sugestões campo a campo e o julgamento do fiscal. Eram
          dois, e conferir uma resposta obrigava a rolar a tela para achar o trecho correspondente
          do parecer — o caminho mais curto para assinar sem ler. */}
      <PainelDoJulgamento chamado={chamado} />
      <CargasDoChamado cargas={chamado.cargas} />
    </div>
  )
}

/** A saída da tela. Mantém a mesma frase nos três estados da página — inclusive no de erro. */
function VoltarParaALista() {
  return (
    <Link
      to={enderecoDaLista('/chamados')}
      className={
        'inline-flex items-center gap-1.5 text-sm text-niteroi-700 underline underline-offset-2 ' +
        'hover:text-niteroi-900'
      }
    >
      <IconeSeta className="h-4 w-4 rotate-180" />
      {TEXTOS.voltarParaALista}
    </Link>
  )
}
